import {useMutation, useQuery} from "@urql/vue";
import {useGtm} from "@gtm-support/vue-gtm";
import {computed} from "vue";
import {useQuery as useSsrQuery} from "./use-query";
import {useHydrated} from "./use-hydrated";
import {useCookie, useRuntimeConfig} from "nuxt/app";
import {
	CartCreateDocument,
	type CartCreateMutation,
	type CartCreateMutationVariables,
	CartProductCreateDocument,
	type CartProductCreateMutation,
	type CartProductCreateMutationVariables,
	CartProductDeleteDocument,
	type CartProductDeleteMutation,
	type CartProductDeleteMutationVariables,
	CartProductUpdateDocument,
	type CartProductUpdateMutation,
	type CartProductUpdateMutationVariables,
	CartUpdatePromotionCodeDocument,
	type CartUpdatePromotionCodeMutation,
	type CartUpdatePromotionCodeMutationVariables,
	CartUpdateGiftCardDocument,
	type CartUpdateGiftCardMutation,
	type CartUpdateGiftCardMutationVariables,
	ViewCartDocument,
	type ViewCartQuery,
	type ViewCartQueryVariables,
} from "../graphql";

// Also the name of the cookie holding the cart's id
export type CartType = "cart" | "wishlist";

/**
 * The visitor's cart or wishlist: its contents and actions for modifying it.
 *
 * By default the contents query is only executed on the client-side after hydration:
 * pages may be cached and served to other visitors (Cloudflare, nuxt SWR), so cart data
 * must not end up in their server-rendered HTML or payload. Use `ssr: true` only on
 * private pages that are never cached, such as the cart page.
 *
 * See our main README for more!
 */
export function useCart(
	type: CartType = "cart",
	{fetchOnSsr = false}: {fetchOnSsr?: boolean} = {},
) {
	const isWishlist = type === "wishlist";

	const {
		public: {appUrl},
	} = useRuntimeConfig();

	const cartCookie = useCookie(type);

	const gtm = useGtm();

	const hydrated = useHydrated();

	const cartQueryVariables = computed(() => ({
		id: cartCookie.value ?? "",
	}));

	// NOTE: Including the query for fetching the cart inside this one composable
	// means that whenever any consumer calls this top-level composable, urql
	// also starts fetching the current cart (for example, product listing cards
	// only call this to *add* items into the cart). However, this isn't a
	// performance issue because URQL de-dupes identical queries, and we would be
	// always fetching the cart data anyway for the header cart button.
	const {data, fetching, stale, error, executeQuery} = (
		fetchOnSsr ? useSsrQuery : useQuery
	)<ViewCartQuery, ViewCartQueryVariables>({
		query: ViewCartDocument,
		variables: cartQueryVariables,
		pause: computed(
			() =>
				cartQueryVariables.value.id.length === 0 ||
				(!fetchOnSsr && !hydrated.value),
		),
	});

	const cartData = computed(() => {
		if (!data.value || data.value.cart.__typename !== "Cart") {
			return undefined;
		}

		return data.value.cart;
	});

	const products = computed(() => cartData.value?.products || []);

	const productCount = computed(() =>
		products.value.reduce(
			(total, cartProduct) => total + cartProduct.quantity,
			0,
		),
	);

	const {executeMutation: executeCreateCart} = useMutation<
		CartCreateMutation,
		CartCreateMutationVariables
	>(CartCreateDocument);

	const {executeMutation: executeCreateCartProduct} = useMutation<
		CartProductCreateMutation,
		CartProductCreateMutationVariables
	>(CartProductCreateDocument);

	const {executeMutation: executeDeleteCartProduct} = useMutation<
		CartProductDeleteMutation,
		CartProductDeleteMutationVariables
	>(CartProductDeleteDocument);

	const {executeMutation: executeCartUpdatePromotionCode} = useMutation<
		CartUpdatePromotionCodeMutation,
		CartUpdatePromotionCodeMutationVariables
	>(CartUpdatePromotionCodeDocument);
	const {executeMutation: executeCartUpdateGiftCard} = useMutation<
		CartUpdateGiftCardMutation,
		CartUpdateGiftCardMutationVariables
	>(CartUpdateGiftCardDocument);

	const {executeMutation: executeUpdateCartProduct} = useMutation<
		CartProductUpdateMutation,
		CartProductUpdateMutationVariables
	>(CartProductUpdateDocument);

	const createCart = async (): Promise<string> => {
		const cartResponse = await executeCreateCart({
			input: {
				isWishlist,
			},
		});

		if (cartResponse.data?.cartCreate.__typename !== "CartCreatePayload") {
			throw new Error("Could not create cart");
		}

		cartCookie.value = cartResponse.data.cartCreate.cart.id;

		return cartResponse.data.cartCreate.cart.id;
	};

	const addProduct = async (productId: string, quantity?: number) => {
		let cartId = cartCookie.value;

		if (!cartId) {
			cartId = await createCart();
		}

		return await executeCreateCartProduct({
			input: {
				cartId,
				productId,
				quantity,
			},
		});
	};

	const removeProduct = async (cartProductId: string): Promise<void> => {
		const {data} = await executeDeleteCartProduct({
			id: cartProductId,
		});

		if (data?.cartProductDelete?.__typename !== "CartProductDeletePayload") {
			return;
		}

		if (data.cartProductDelete.cart.products.length === 0) {
			gtm?.trackEvent({
				event: "empty_cart_manually",
				_clear: true,
				ecommerce: {
					cart_ref: data.cartProductDelete.cart.id,
				},
			});
		}
	};

	const updateProduct = async (
		cartProductId: string,
		quantity: number,
	): Promise<void> => {
		if (quantity <= 0) {
			await removeProduct(cartProductId);

			return;
		}

		await executeUpdateCartProduct({
			id: cartProductId,
			quantity,
		});
	};

	const setPromotionCode = async (promotionCode: string): Promise<boolean> => {
		let cartId = cartCookie.value;

		if (!cartId) {
			cartId = await createCart();
		}

		const promoCodeUpdateResponse = await executeCartUpdatePromotionCode({
			id: cartId,
			promotionCode,
		});

		if (
			promoCodeUpdateResponse.data?.cartUpdatePromotionCode.__typename !==
			"CartUpdatePayload"
		) {
			throw new Error("Could not update the promotion code");
		}

		return !!promoCodeUpdateResponse.data.cartUpdatePromotionCode.cart
			?.promotionCode;
	};

	const clearPromotionCode = async (): Promise<void> => {
		let cartId = cartCookie.value;

		if (!cartId) {
			cartId = await createCart();
		}

		const promoCodeUpdateResponse = await executeCartUpdatePromotionCode({
			id: cartId,
			promotionCode: null,
		});

		if (
			promoCodeUpdateResponse.data?.cartUpdatePromotionCode.__typename !==
			"CartUpdatePayload"
		) {
			throw new Error("Could not clear the promotion code");
		}
	};

	const setGiftCardCode = async (giftCardCode: string): Promise<boolean> => {
		let cartId = cartCookie.value;

		if (!cartId) {
			cartId = await createCart();
		}

		const giftCardUpdateResponse = await executeCartUpdateGiftCard({
			id: cartId,
			giftCardCode,
		});

		if (
			giftCardUpdateResponse.data?.cartUpdateGiftCard.__typename !==
			"CartUpdatePayload"
		) {
			throw new Error("Could not update the gift card code");
		}

		return !!giftCardUpdateResponse.data.cartUpdateGiftCard.cart?.giftCard;
	};

	const clearGiftCard = async (): Promise<void> => {
		let cartId = cartCookie.value;

		if (!cartId) {
			cartId = await createCart();
		}

		const giftCardUpdateResponse = await executeCartUpdateGiftCard({
			id: cartId,
			giftCardCode: null,
		});

		if (
			giftCardUpdateResponse.data?.cartUpdateGiftCard.__typename !==
			"CartUpdatePayload"
		) {
			throw new Error("Could not clear the gift card");
		}
	};
	// For completely resetting the cart upon natural events, i.e. when making a purchase.
	// If a manual "empty cart" button is needed, we should probably define a new method that clears the existing cart and doesn't just throw away the cookie - otherwise the cleared cart might look like an abandoned cart.
	const reset = () => {
		gtm?.trackEvent({
			event: "empty_cart",
			_clear: true,
			ecommerce: {
				cart_ref: cartCookie.value || "",
				cart_url: cartCookie.value
					? `${appUrl}/cart/${cartCookie.value}/switch`
					: "",
			},
		});

		cartCookie.value = null;
	};

	return {
		id: computed(() => cartCookie.value),
		// Raw cart fetch data
		data,
		// Actual cart data
		cart: cartData,
		fetching,
		stale,
		error,
		executeQuery,
		products,
		productCount,
		addProduct,
		removeProduct,
		updateProduct,
		setPromotionCode,
		clearPromotionCode,
		setGiftCardCode,
		clearGiftCard,
		reset,
	};
}
